/**
 * Campaign Sample Generation Module
 *
 * Handles sample email generation progress tracking and UI updates.
 *
 * @module components/campaign-sample
 */

import { getConfig, getCsrfToken } from '@/utils/template-data.js';

// Module state
let progressInterval = null;

/**
 * Convert a value to boolean (handles string "true"/"false" from Django templates).
 * @param {*} value - Value to convert
 * @returns {boolean}
 */
function toBool(value) {
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return Boolean(value);
}

/**
 * Initialize campaign sample functionality.
 * @param {Object} config - Configuration from template
 */
export function initCampaignSample(config) {
  const {
    campaignId,
    campaignStatus,
    sampleSize,
    sampleUrl,
    detailUrl
  } = config;

  // Convert string booleans from Django templates to actual booleans
  const isGenerating = toBool(config.isGenerating);
  const autoGenerate = toBool(config.autoGenerate);
  const hasPageObj = toBool(config.hasPageObj);

  // Store config for use in other functions (with corrected booleans)
  window._campaignSampleConfig = {
    ...config,
    isGenerating,
    autoGenerate,
    hasPageObj
  };

  console.log('[Campaign Sample] Config:', { campaignId, isGenerating, autoGenerate, hasPageObj, campaignStatus });

  // Auto-start generation if coming from wizard
  if (autoGenerate && !isGenerating && !hasPageObj) {
    // Small delay to let page render first
    setTimeout(() => {
      startSampleGeneration();
    }, 500);
  } else if (isGenerating || ['sampling', 'generating_all', 'review_pending'].includes(campaignStatus)) {
    // If already generating (sample or full), start tracking
    startProgressTracking();
  }

  // Attach click handler to generate button
  const generateBtn = document.getElementById('generateBtn');
  if (generateBtn) {
    generateBtn.addEventListener('click', function (e) {
      e.preventDefault();
      startSampleGeneration();
    });
  }

  // Cleanup on page unload
  window.addEventListener('beforeunload', cleanup);

  // Expose functions globally
  window.startSampleGeneration = startSampleGeneration;
}

/**
 * Start sample generation.
 */
export function startSampleGeneration() {
  const config = window._campaignSampleConfig || getConfig('campaign-sample');

  // Show progress UI immediately
  showProgressUI(config);

  // Submit generation request via AJAX
  fetch(config.sampleUrl || window.location.href, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-CSRFToken': getCsrfToken(),
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: 'action=generate'
  })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Start progress tracking
        startProgressTracking();
      } else {
        alert('Failed to start generation: ' + (data.error || 'Unknown error'));
        location.reload();
      }
    })
    .catch(error => {
      console.error('Failed to start generation:', error);
      alert('Failed to start sample generation. Please try again.');
      location.reload();
    });
}

/**
 * Start progress tracking interval.
 */
export function startProgressTracking() {
  // Poll every 2 seconds during sample generation
  if (!progressInterval) {
    progressInterval = setInterval(updateProgress, 2000);
  }

  // Show progress UI if not already shown
  if (!document.getElementById('sample-progress-bar')) {
    const config = window._campaignSampleConfig || getConfig('campaign-sample');
    showProgressUI(config);
  }
}

/**
 * Update progress from server.
 */
function updateProgress() {
  const config = window._campaignSampleConfig || getConfig('campaign-sample');

  fetch(window.location.href, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest'
    }
  })
    .then(response => response.json())
    .then(data => {
      console.log('[Progress Update]', data);

      // Update progress bar
      const progressBar = document.getElementById('sample-progress-bar');
      const progressText = document.getElementById('sample-progress-text');
      const progressCount = document.getElementById('sample-progress-count');

      if (progressBar) {
        progressBar.style.width = `${data.percentage || 0}%`;
        progressBar.setAttribute('aria-valuenow', data.percentage || 0);
      }

      if (progressText) {
        progressText.textContent = `${Math.round(data.percentage || 0)}%`;
      }

      if (progressCount && data.current_count !== undefined && data.total_expected !== undefined) {
        progressCount.innerHTML = `<small>${data.current_count} of ${data.total_expected} emails generated</small>`;
      }

      // Update stats
      updateElementText('emails-generated', data.current_count);
      updateElementText('emails-expected', data.total_expected);
      updateElementText('total-contacts', data.sample_size || data.validated_sample_size || config.sampleSize);

      // Update time remaining from server estimate
      updateTimeRemaining(data.percentage, data.estimated_seconds_remaining);

      // Handle validation issues
      if (data.has_validation_issues && !document.getElementById('validation-warning')) {
        showValidationWarning(data);
      }

      // Check if complete
      if (data.is_complete || data.phase === 'reviewing' || data.phase === 'pending_approval' || data.status === 'pending_approval') {
        clearInterval(progressInterval);
        progressInterval = null;

        // Reload to show the generated emails
        setTimeout(() => {
          location.reload();
        }, 1000);
      }

      // Handle validation failure
      if (data.all_invalid || data.phase === 'failed_validation') {
        clearInterval(progressInterval);
        progressInterval = null;
        showValidationFailure(data, config);
      }

      // Update sample emails preview if provided
      if (data.sample_emails && data.sample_emails.length > 0) {
        updateSampleEmailsPreview(data.sample_emails);
      }
    })
    .catch(error => {
      console.error('Failed to update progress:', error);
    });
}

/**
 * Show progress UI.
 */
function showProgressUI(config) {
  // Replace the generate button area with progress UI
  const generateSection = document.getElementById('generate-section');
  if (!generateSection) return;

  const sampleSize = config?.sampleSize || '--';

  generateSection.innerHTML = `
    <div class="card status-card info">
      <div class="card-body text-center py-5">
        <div class="status-icon-container">
          <div class="status-icon primary">
            <i class="fas fa-cog icon-spin"></i>
          </div>
          <div class="status-ring primary"></div>
        </div>

        <h3 class="mb-3" id="status-title">
          <span class="text-gradient">Generating Sample Emails...</span>
        </h3>

        <p class="status-message" id="status-description">
          Validating email addresses and generating personalized content...
        </p>

        <div class="progress-section">
          <h5><i class="fas fa-tasks me-2"></i>Generation Progress</h5>
          <div class="progress mb-4">
            <div id="sample-progress-bar"
               class="progress-bar progress-bar-striped progress-bar-animated"
               role="progressbar"
               style="width: 0%"
               aria-valuenow="0"
               aria-valuemin="0"
               aria-valuemax="100">
              <span id="sample-progress-text">0%</span>
            </div>
          </div>

          <div class="stat-grid">
            <div class="stat-box">
              <div class="stat-value" id="total-contacts">${sampleSize}</div>
              <div class="stat-label">Valid Contacts</div>
            </div>
            <div class="stat-box">
              <div class="stat-value text-primary" id="emails-generated">0</div>
              <div class="stat-label">Generated</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" id="emails-expected">--</div>
              <div class="stat-label">Expected</div>
            </div>
            <div class="stat-box">
              <div class="stat-value text-success" id="time-remaining">
                <span class="skeleton-loader" style="display: inline-block; width: 80px; height: 30px;"></span>
              </div>
              <div class="stat-label">Time Left</div>
            </div>
          </div>
        </div>

        <p class="text-muted mt-3" id="sample-progress-count">
          <small>This typically takes 30-60 seconds. Email validation is happening in the background.</small>
        </p>

        <div id="sample-emails-preview"></div>
      </div>
    </div>
  `;
}

/**
 * Show validation warning.
 */
function showValidationWarning(data) {
  const progressSection = document.querySelector('.progress-section');
  if (progressSection) {
    const warningDiv = document.createElement('div');
    warningDiv.id = 'validation-warning';
    warningDiv.className = 'alert alert-warning mt-3';
    warningDiv.innerHTML = `
      <div class="d-flex align-items-start">
        <i class="fas fa-exclamation-triangle me-3 mt-1" style="font-size: 1.5em;"></i>
        <div>
          <strong>Email Validation Notice</strong>
          <p class="mb-0 mt-2">
            ${escapeHtml(data.validation_message || data.skipped_count + ' email(s) failed validation and were removed.')}
          </p>
        </div>
      </div>
    `;
    progressSection.parentNode.insertBefore(warningDiv, progressSection.nextSibling);
  }
}

/**
 * Show validation failure.
 */
function showValidationFailure(data, config) {
  const generateSection = document.getElementById('generate-section');
  if (!generateSection) return;

  const detailUrl = config?.detailUrl || `/campaigns/${config?.campaignId}/`;

  generateSection.innerHTML = `
    <div class="card status-card danger">
      <div class="card-body text-center py-5">
        <div class="status-icon-container">
          <div class="status-icon danger">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <div class="status-ring danger"></div>
        </div>

        <h3 class="mb-3">Validation Failed</h3>
        <p class="status-message">
          ${escapeHtml(data.message || 'All sample emails failed validation. Please check your contact list for valid email addresses.')}
        </p>

        <div class="alert alert-danger mt-4">
          <strong><i class="fas fa-exclamation-circle me-2"></i>Error:</strong><br>
          ${escapeHtml(data.message || 'All emails failed validation')}
        </div>

        <div class="mt-4">
          <a href="${detailUrl}" class="btn btn-danger btn-lg">
            <i class="fas fa-arrow-left me-2"></i>Back to Campaign
          </a>
        </div>
      </div>
    </div>
  `;
}

/**
 * Update sample emails preview.
 */
function updateSampleEmailsPreview(emails) {
  const previewContainer = document.getElementById('sample-emails-preview');
  if (!previewContainer) return;

  // Group emails by contact
  const contactGroups = {};
  emails.forEach(email => {
    if (!contactGroups[email.contact_email]) {
      contactGroups[email.contact_email] = {
        name: email.contact_name,
        company: email.contact_company,
        emails: []
      };
    }
    contactGroups[email.contact_email].emails.push(email);
  });

  // Clear and rebuild preview
  previewContainer.innerHTML = '<hr class="my-4"><h5 class="mb-3">Generating emails for:</h5>';

  Object.keys(contactGroups).forEach(contactEmail => {
    const contact = contactGroups[contactEmail];
    const contactCard = document.createElement('div');
    contactCard.className = 'card mb-3 fade-in';
    contactCard.innerHTML = `
      <div class="card-header">
        <strong>${escapeHtml(contact.name)}</strong> - ${escapeHtml(contactEmail)}
        ${contact.company ? ` (${escapeHtml(contact.company)})` : ''}
      </div>
      <div class="card-body">
        <p class="text-muted mb-0">
          <i class="fas fa-check-circle text-success me-1"></i>
          Generating ${contact.emails.length} email(s)...
        </p>
      </div>
    `;
    previewContainer.appendChild(contactCard);
  });
}

/**
 * Update element text content.
 */
function updateElementText(elementId, value) {
  const element = document.getElementById(elementId);
  if (element && value !== undefined && value !== null) {
    element.textContent = typeof value === 'number' ? value.toLocaleString() : value;
  }
}

/**
 * Format seconds into a human-readable time string.
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string
 */
function formatTimeRemaining(seconds) {
  if (seconds < 60) {
    return '< 1 min';
  }
  const minutes = Math.ceil(seconds / 60);
  if (minutes <= 60) {
    return `~${minutes} min`;
  }
  const hours = Math.ceil(minutes / 60);
  return hours === 1 ? '~1 hour' : `~${hours} hours`;
}

/**
 * Update time remaining display from server-provided estimate.
 * @param {number} percentage - Current progress percentage
 * @param {number|null} estimatedSeconds - Server-calculated seconds remaining
 */
function updateTimeRemaining(percentage, estimatedSeconds) {
  const timeElement = document.getElementById('time-remaining');
  if (!timeElement) return;

  // Show check mark when complete
  if (percentage >= 100) {
    timeElement.innerHTML = '<i class="fas fa-check"></i>';
    return;
  }

  // Don't show anything if no progress yet
  if (percentage <= 0) {
    return;
  }

  // Display server-provided estimate
  if (estimatedSeconds !== null && estimatedSeconds !== undefined && estimatedSeconds > 0) {
    timeElement.textContent = formatTimeRemaining(estimatedSeconds);
  } else {
    timeElement.textContent = 'Calculating...';
  }
}

/**
 * Escape HTML to prevent XSS.
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Cleanup on page unload.
 */
function cleanup() {
  if (progressInterval) {
    clearInterval(progressInterval);
    progressInterval = null;
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('[Campaign Sample] DOMContentLoaded fired');
  const config = getConfig('campaign-sample');
  console.log('[Campaign Sample] Raw config from getConfig:', config);
  if (config.campaignId) {
    console.log('[Campaign Sample] Initializing with campaignId:', config.campaignId);
    initCampaignSample(config);
  } else {
    console.log('[Campaign Sample] No campaignId found, skipping initialization');
  }
});

// Export for module usage
export default {
  initCampaignSample,
  startSampleGeneration,
  startProgressTracking,
};

// Named exports for testing (functions not already exported at definition)
export {
  toBool,
  formatTimeRemaining,
  updateTimeRemaining,
};

// Test helpers - allow tests to check/reset internal state
export function getProgressIntervalForTesting() {
  return progressInterval;
}

export function clearProgressIntervalForTesting() {
  if (progressInterval) {
    clearInterval(progressInterval);
    progressInterval = null;
  }
}

/**
 * Check if a campaign status should trigger progress tracking.
 * Exported for testing.
 * @param {string} status - Campaign status
 * @returns {boolean}
 */
export function shouldTrackProgress(status) {
  return ['sampling', 'generating_all', 'review_pending'].includes(status);
}
